import { useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box, IconButton, Tooltip, HStack, useToast } from "@chakra-ui/react";
import { HiEye, HiDocumentDuplicate } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

export default function CodifySpecificationsGrid({ codifySpecifications, loading, onCopy }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [copying, setCopying] = useState(null);

  const handleCopy = async (specification) => {
    setCopying(specification._id);
    try {
      // First, fetch the full specification
      const fetchResponse = await fetch(
        `https://codify.poc.mrc.ai/api/specifications/${specification._id}`
      );

      if (!fetchResponse.ok) {
        throw new Error("Failed to fetch full specification");
      }

      const fullSpecification = await fetchResponse.json();

      // Create a deep copy of the full specification with a new ID and updated metadata
      const copiedSpecification = {
        ...fullSpecification,
        _id: crypto.randomUUID(),
        name: `${fullSpecification.name} (Copy)`,
        createdDate: new Date().toISOString(),
      };

      // Save the new specification to the API
      const response = await fetch("https://codify.poc.mrc.ai/api/specifications", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(copiedSpecification),
      });

      if (!response.ok) {
        throw new Error("Failed to copy specification");
      }

      const result = await response.json();

      toast({
        title: "Specification copied",
        description: `Successfully copied "${fullSpecification.name}"`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Navigate to the new specification
      navigate(`/codifyspecifications/${result._id}`);

      // Call the onCopy callback if provided
      if (onCopy) {
        onCopy(result);
      }
    } catch (error) {
      console.error("Error copying specification:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to copy specification",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCopying(null);
    }
  };

  const ActionsCellRenderer = useMemo(
    () => (props) => {
      const isCopying = copying === props.data._id;
      return (
        <HStack spacing={2} height="100%" alignItems="center">
          <Tooltip label="View Codify Specification">
            <IconButton
              icon={<HiEye />}
              size="sm"
              aria-label="View specification"
              onClick={() => navigate(`/codifyspecifications/${props.data._id}`)}
            />
          </Tooltip>
          <Tooltip label="Copy Codify Specification">
            <IconButton
              icon={<HiDocumentDuplicate />}
              size="sm"
              aria-label="Copy specification"
              onClick={() => handleCopy(props.data)}
              isLoading={isCopying}
              disabled={isCopying}
            />
          </Tooltip>
        </HStack>
      );
    },
    [navigate, copying]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "codifyCollection",
        headerName: "Collection",
        filter: "agTextColumnFilter",
        rowGroup: true,
        hide: false,
        sort: "asc",
        width: 150,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCellRenderer,
        width: 120,
        sortable: false,
        filter: false,
      },
      {
        field: "name",
        headerName: "Name",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "description",
        headerName: "Description",
        filter: "agTextColumnFilter",
        flex: 2,
        minWidth: 300,
      },
      {
        field: "status",
        headerName: "Status",
        filter: "agTextColumnFilter",
        width: 120,
      },
      {
        field: "dataStandard",
        headerName: "Data Standard",
        filter: "agTextColumnFilter",
        width: 150,
      },
      {
        field: "dataType",
        headerName: "Data Type",
        filter: "agTextColumnFilter",
        width: 150,
      },
    ],
    [ActionsCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Collection",
      minWidth: 250,
      cellRendererParams: {
        suppressCount: false,
      },
    }),
    []
  );

  return (
    <Box
      height="100%"
      width="100%"
      className="ag-theme-alpine"
    >
      <AgGridReact
        rowData={codifySpecifications}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDefaultExpanded={1}
        animateRows={true}
        pagination={true}
        paginationPageSize={20}
        loading={loading}
        rowHeight={35}
        headerHeight={40}
      />
    </Box>
  );
}
